import {
    useState,
    useCallback,
    memo,
    useEffect,
    useRef,
    useMemo,
} from 'react'
import {
    // Tooltip,
    // Box,
    // Typography,
    IconButton,
    // LinearProgress,
    Grid,
    Tooltip,
    Typography,
    CircularProgress,
    // CircularProgress,
} from '@mui/material'
import {
    GoogleMap,
    OverlayView,
    OverlayViewF,
    PolygonF,
} from '@react-google-maps/api'
import { GoogleMapsWrapper } from '../hooks/GoogleMapsApiWrapper'
import {
    RefreshCw,
    Moon,
    Layers,
    Crosshair,
    Maximize,
    Ungroup,
    Contrast,
} from 'lucide-react'

// Google Maps dark theme (applies to the roadmap base; satellite is imagery).
const DARK_MAP_STYLE: google.maps.MapTypeStyle[] = [
    { elementType: 'geometry', stylers: [{ color: '#212121' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
    {
        featureType: 'administrative',
        elementType: 'geometry',
        stylers: [{ color: '#757575' }],
    },
    {
        featureType: 'administrative.country',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#9e9e9e' }],
    },
    {
        featureType: 'administrative.locality',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#bdbdbd' }],
    },
    {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [{ color: '#181818' }],
    },
    {
        featureType: 'road',
        elementType: 'geometry.fill',
        stylers: [{ color: '#2c2c2c' }],
    },
    {
        featureType: 'road',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#8a8a8a' }],
    },
    {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [{ color: '#3c3c3c' }],
    },
    {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#000000' }],
    },
    {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#3d3d3d' }],
    },
]
import { Sparkline } from '../../Analytics/Dashboard/components/Sparkline'
import { useGlobalState } from '../../../context/GlobalStateContext'

interface FleetGpsObject {
    lat: number
    lng: number
    name: string
    status?: string
    color?: string
    count?: number
    manufacturer?: string
    model?: string
    alias?: string
    serial?: string
    // Per-status breakdown for a multi-boat cluster (tooltip).
    statuses?: Record<string, number>
}

// Lazily-loaded per-boat detail for the hover tooltip. Signals are derived
// from the boat's signal profile (friendly name + unit), not raw variables.
export interface BoatDetail {
    serial: string
    nickname?: string | null
    hin?: string | null
    model?: string | null
    modelYear?: number | null
    dealer?: string | null
    lastSeen?: number | null
    signals?: {
        label: string
        value: number
        unit?: string
        kind?: 'battery' | 'engineHours'
    }[]
    // Engine-hours sparkline for the period (+ alarm/DTC day markers).
    hoursSeries?: number[]
    hoursDays?: string[]
    alarms?: number[]
    dtcs?: number[]
    error?: boolean
}

// Browser-local "last heard from" + dynamic relative age.
const formatLastSeen = (ms: number) => {
    const local = new Date(ms).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
    const diff = Math.max(0, Date.now() - ms)
    const s = Math.floor(diff / 1000)
    let ago: string
    if (s < 60) ago = `${s}s ago`
    else if (s < 3600) {
        const m = Math.floor(s / 60)
        ago = `${m} min${m > 1 ? 's' : ''} ago`
    } else if (s < 86400) {
        const h = Math.floor(s / 3600)
        ago = `${h} hr${h > 1 ? 's' : ''} ago`
    } else {
        const d = Math.floor(s / 86400)
        if (d < 30) ago = `${d} day${d > 1 ? 's' : ''} ago`
        else if (d < 365) {
            const mo = Math.floor(d / 30)
            ago = `${mo} month${mo > 1 ? 's' : ''} ago`
        } else {
            const y = Math.floor(d / 365)
            ago = `${y} yr${y > 1 ? 's' : ''} ago`
        }
    }
    return `${local} | ${ago}`
}

// Battery health color by 12V thresholds: <11 red, 11-11.8 amber, 11.8-15
// green, 15+ red (overcharge).
const batteryColor = (v: number) =>
    v < 11
        ? '#E00000'
        : v < 11.8
        ? '#F5A623'
        : v < 15
        ? '#8BC441'
        : '#E00000'

interface UniversalGoogleMapProps {
    gps?: { lat: string; lng: string }
    style: {
        width?: string | number
        height?: string | number
        backgroundColor?: string
        margin?: string | number
    }
    fleetGps?: FleetGpsObject[]
    mapZoom?: number
    defaultCenter?: { lat: number; lng: number }
    onAssetClick?: (asset: FleetGpsObject) => void
    // When provided, hovering a single boat lazily loads its detail.
    fetchBoatDetail?: (serial: string) => Promise<BoatDetail | null>
    // Status label for the single `gps` marker (colours it via STATUS_DOT).
    gpsStatus?: string
    // Suppress the hover tooltip (used for the single-boat insights map).
    disableTooltip?: boolean
    // External focus request: pan + zoom the map to this point (e.g. when a
    // boat row is clicked in the dealer "Top Performers" table). Pass a NEW
    // object reference each time to re-trigger even for the same coordinates.
    // When `id` (a boat serial) is supplied, the map also declusters and selects
    // that pin so its tooltip opens.
    focusPoint?: { lat: number; lng: number; id?: string } | null
    // Bump this number to snap the map back to its default view (e.g. when a
    // Service row is collapsed).
    resetView?: number
    // Start with pins declustered (individual) instead of grouped. Still
    // auto-clusters when more than `maxDeclustered` boats are in view.
    defaultClustered?: boolean
    // Cap above which declustering is force-disabled (default 50). Pass a large
    // value (or Infinity) to always allow individual pins.
    maxDeclustered?: number
    // Initial base layer (default roadmap). The user can still toggle.
    defaultMapType?: 'roadmap' | 'satellite'
}

interface CustomMarkerProps {
    position: { lat: number; lng: number }
    asset: FleetGpsObject
    onClick: (asset: FleetGpsObject) => void
    fetchBoatDetail?: (serial: string) => Promise<BoatDetail | null>
    disableTooltip?: boolean
    beacon?: boolean
    // Externally force the tooltip open (e.g. when this boat's row is clicked).
    forceOpen?: boolean
}

// ---- Day/night terminator -------------------------------------------------
// Computes the night-side polygon for the current time (subsolar point + great
// circle), so we can shade the half of the globe in darkness. Pure math — no
// dependency. (Astronomy adapted from the MIT-licensed L.Terminator.)
const RAD = Math.PI / 180
const julianDay = (date: Date) => date.getTime() / 86400000 + 2440587.5
const gmstHours = (jd: number) =>
    (18.697374558 + 24.06570982441908 * (jd - 2451545.0)) % 24
const sunEclipticLon = (jd: number) => {
    const n = jd - 2451545.0
    const L = (280.46 + 0.9856474 * n) % 360
    const g = (357.528 + 0.9856003 * n) % 360
    return L + 1.915 * Math.sin(g * RAD) + 0.02 * Math.sin(2 * g * RAD)
}
const eclipticObliquity = (jd: number) =>
    23.43929111 - ((jd - 2451545.0) / 36525) * (46.836769 / 3600)
const computeTerminator = (date: Date): { lat: number; lng: number }[] => {
    const jd = julianDay(date)
    const gst = gmstHours(jd)
    const lambda = sunEclipticLon(jd)
    const obliq = eclipticObliquity(jd)
    const alpha =
        Math.atan2(
            Math.cos(obliq * RAD) * Math.sin(lambda * RAD),
            Math.cos(lambda * RAD)
        ) / RAD
    const delta =
        Math.asin(Math.sin(obliq * RAD) * Math.sin(lambda * RAD)) / RAD
    const path: { lat: number; lng: number }[] = []
    for (let lng = -180; lng <= 180; lng += 2) {
        const hourAngle = (gst + lng / 15) * 15 - alpha
        const lat =
            Math.atan(-Math.cos(hourAngle * RAD) / Math.tan(delta * RAD)) / RAD
        path.push({ lat, lng })
    }
    // Close the loop along whichever pole is currently in darkness. Step the
    // pole edge in 2deg increments (lng 180 -> -180): a single 360deg-span
    // segment gets rendered "the short way" by Google Maps and fills the wrong
    // hemisphere, so we walk the edge explicitly to keep the night side filled.
    const darkPole = delta > 0 ? -90 : 90
    for (let lng = 180; lng >= -180; lng -= 2) {
        path.push({ lat: darkPole, lng })
    }
    return path
}

// Brand status colors (match the fleet status bar) for tooltip dots.
const STATUS_DOT: Record<string, string> = {
    Online: '#8BC441',
    Running: '#E00000',
    Awake: '#AA3E98',
    Shutdown: '#508991',
    Offline: '#9AA0A6',
}

// Module-level (was re-created per marker on every render).
const getStatusColor = (status?: string) => {
    switch (status?.toUpperCase()) {
        case 'ONLINE':
        case 'RUNNING':
            return '#4caf50'
        case 'SHUTDOWN':
            return '#ddd5d5ff'
        case 'AWAKE':
            return '#ff9800'
        case 'OFFLINE':
        case 'IDLE':
            return '#9e9e9e'
        default:
            return '#9e9e9e'
    }
}

// Max individual pins we'll render at once. Above this many boats in the
// viewport, declustering is suppressed (and the toggle disabled) — rendering
// hundreds of individual markers makes the map crawl. The user can zoom in to
// drop below the threshold and individual pins return automatically.
const MAX_DECLUSTERED = 50

// Zoom-adaptive grid clustering: cell size shrinks as you zoom in, so pins
// split apart down to individual boats at high zoom. Only the points passed in
// (already viewport-culled) are clustered, keeping it fast at fleet scale.
const clusterPoints = (
    pts: FleetGpsObject[],
    zoom: number
): FleetGpsObject[] => {
    if (!pts.length) return pts
    // Degrees per cell — halves each zoom level. Divisor tunes pin spacing.
    const cell = 360 / Math.pow(2, zoom) / 3
    if (!isFinite(cell) || cell <= 0) return pts
    const cells = new Map<
        string,
        {
            latSum: number
            lngSum: number
            count: number
            statuses: Record<string, number>
            colorOf: Record<string, string>
            sample: FleetGpsObject
        }
    >()
    for (const p of pts) {
        const key = `${Math.floor(p.lat / cell)}_${Math.floor(p.lng / cell)}`
        let c = cells.get(key)
        if (!c) {
            c = {
                latSum: 0,
                lngSum: 0,
                count: 0,
                statuses: {},
                colorOf: {},
                sample: p,
            }
            cells.set(key, c)
        }
        c.latSum += p.lat
        c.lngSum += p.lng
        c.count++
        const s = p.status || 'Unknown'
        c.statuses[s] = (c.statuses[s] || 0) + 1
        if (p.color) c.colorOf[s] = p.color
    }
    const out: FleetGpsObject[] = []
    cells.forEach((c) => {
        if (c.count === 1) {
            out.push({ ...c.sample, count: 1 })
            return
        }
        let dom = ''
        let domN = -1
        for (const [s, n] of Object.entries(c.statuses)) {
            if (n > domN) {
                domN = n
                dom = s
            }
        }
        out.push({
            lat: c.latSum / c.count,
            lng: c.lngSum / c.count,
            name: `${c.count} boats`,
            status: dom,
            color: c.colorOf[dom] || '#9AA0A6',
            count: c.count,
            statuses: c.statuses,
        })
    })
    return out
}

// memo: a cluster only re-renders when its own props change, so hovering /
// selecting one marker no longer re-renders every other marker on the map.
const CustomMarker = memo(function CustomMarker({
    position,
    asset,
    onClick,
    fetchBoatDetail,
    disableTooltip,
    beacon,
    forceOpen,
}: CustomMarkerProps) {
    const [isHovered, setIsHovered] = useState(false)
    const [pinned, setPinned] = useState(false)
    const [detail, setDetail] = useState<BoatDetail | null>(null)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const showTooltip = !disableTooltip && (isHovered || pinned || !!forceOpen)

    // Cluster sizing: bigger dot for bigger clusters, with a count label.
    const count = asset.count ?? 1
    const isSingle = count === 1
    const size = Math.max(18, Math.min(46, 16 + Math.log2(count + 1) * 6))
    const countLabel =
        count > 999
            ? `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}k`
            : count > 1
            ? `${count}`
            : ''

    const dotRef = useRef<HTMLDivElement>(null)
    // Tooltip placement: flips below the pin when there's no room above (pin
    // near the top edge) and nudges horizontally near the left/right edges, so
    // it never spills outside the map and gets clipped.
    const [tip, setTip] = useState<{ vertical: 'top' | 'bottom'; dx: number }>({
        vertical: 'top',
        dx: 0,
    })
    const computePlacement = () => {
        const el = dotRef.current
        const mapEl = el?.closest('.gm-style') as HTMLElement | null
        if (!el || !mapEl) return
        const r = el.getBoundingClientRect()
        const m = mapEl.getBoundingClientRect()
        const TIP_H = 250 // generous estimate; exact height not needed to decide
        const TIP_HALF = 150 // half of the tooltip's maxWidth (300)
        const spaceAbove = r.top - m.top
        const spaceBelow = m.bottom - r.bottom
        const vertical: 'top' | 'bottom' =
            spaceAbove < TIP_H + 16 && spaceBelow > spaceAbove ? 'bottom' : 'top'
        const centerX = r.left + r.width / 2
        let dx = 0
        if (centerX - TIP_HALF < m.left + 8)
            dx = m.left + 8 - (centerX - TIP_HALF)
        else if (centerX + TIP_HALF > m.right - 8)
            dx = m.right - 8 - (centerX + TIP_HALF)
        setTip({ vertical, dx })
    }

    // Lazily load detail for an individual boat (once).
    const loadDetail = () => {
        if (isSingle && asset.serial && fetchBoatDetail && !detail) {
            setLoadingDetail(true)
            fetchBoatDetail(asset.serial)
                .then((d) => setDetail(d))
                .finally(() => setLoadingDetail(false))
        }
    }
    const handleEnter = () => {
        computePlacement()
        setIsHovered(true)
        loadDetail()
    }
    // Clicking a single boat pins the tooltip open (click again to dismiss);
    // clusters bubble up to zoom in.
    const handleDotClick = () => {
        if (isSingle) {
            computePlacement()
            setPinned((p) => !p)
            loadDetail()
        }
        onClick(asset)
    }

    // Externally-focused boat: place + load the tooltip when it's forced open.
    useEffect(() => {
        if (forceOpen && isSingle) {
            computePlacement()
            loadDetail()
        }
    }, [forceOpen, isSingle])

    return (
        <OverlayViewF
            position={position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
            <div style={{ position: 'relative' }}>
                {showTooltip && (
                    <div
                        style={{
                            position: 'absolute',
                            ...(tip.vertical === 'top'
                                ? { bottom: '28px' }
                                : { top: '28px' }),
                            left: '50%',
                            transform: `translateX(calc(-50% + ${tip.dx}px))`,
                            background:
                                'linear-gradient(180deg, rgba(30,32,36,0.97), rgba(18,20,23,0.97))',
                            color: '#fff',
                            borderRadius: 10,
                            zIndex: 1000,
                            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                            border: '1px solid rgba(255,255,255,0.10)',
                            backdropFilter: 'blur(6px)',
                            minWidth: 200,
                            maxWidth: 300,
                            overflow: 'hidden',
                            fontSize: 12,
                            lineHeight: 1.45,
                        }}
                    >
                        {!isSingle ? (
                            // Cluster: count + per-status breakdown.
                            <div style={{ padding: '10px 12px' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: 6,
                                        marginBottom: 6,
                                    }}
                                >
                                    <span
                                        style={{ fontSize: 16, fontWeight: 800 }}
                                    >
                                        {count.toLocaleString()}
                                    </span>
                                    <span style={{ opacity: 0.65 }}>
                                        boats in view
                                    </span>
                                </div>
                                {asset.statuses &&
                                    Object.entries(asset.statuses)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([s, n]) => (
                                            <div
                                                key={s}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    gap: 16,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            background:
                                                                STATUS_DOT[s] ||
                                                                '#9AA0A6',
                                                        }}
                                                    />
                                                    {s}
                                                </span>
                                                <span
                                                    style={{ fontWeight: 700 }}
                                                >
                                                    {n.toLocaleString()}
                                                </span>
                                            </div>
                                        ))}
                                <div
                                    style={{
                                        marginTop: 6,
                                        opacity: 0.55,
                                        fontSize: 11,
                                    }}
                                >
                                    Click to zoom in →
                                </div>
                            </div>
                        ) : (
                            // Single boat: lazily-loaded, profile-derived detail.
                            <>
                                <div
                                    style={{
                                        padding: '10px 12px',
                                        borderBottom:
                                            '1px solid rgba(255,255,255,0.08)',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 10,
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontWeight: 800,
                                                fontSize: 13,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: 180,
                                            }}
                                        >
                                            {detail?.nickname ||
                                                detail?.hin ||
                                                asset.serial ||
                                                'Boat'}
                                        </span>
                                        <span
                                            style={{
                                                flexShrink: 0,
                                                fontSize: 10,
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                                padding: '2px 8px',
                                                borderRadius: 999,
                                                color: '#fff',
                                                background:
                                                    asset.color || '#9AA0A6',
                                            }}
                                        >
                                            {asset.status || 'unknown'}
                                        </span>
                                    </div>
                                    {(detail?.hin ||
                                        detail?.serial ||
                                        asset.serial) && (
                                        <div
                                            style={{
                                                opacity: 0.6,
                                                fontSize: 11,
                                                marginTop: 2,
                                                wordBreak: 'break-all',
                                            }}
                                        >
                                            {[
                                                detail?.hin,
                                                detail?.serial || asset.serial,
                                            ]
                                                .filter(Boolean)
                                                .join(' | ')}
                                        </div>
                                    )}
                                    {(detail?.modelYear || detail?.model) && (
                                        <div
                                            style={{
                                                opacity: 0.85,
                                                marginTop: 3,
                                            }}
                                        >
                                            {[detail?.modelYear, detail?.model]
                                                .filter(Boolean)
                                                .join(' ')}
                                        </div>
                                    )}
                                    {detail?.dealer && (
                                        <div
                                            style={{
                                                opacity: 0.55,
                                                fontSize: 11,
                                                marginTop: 1,
                                            }}
                                        >
                                            {detail.dealer}
                                        </div>
                                    )}
                                    {detail?.lastSeen ? (
                                        <div
                                            style={{
                                                opacity: 0.5,
                                                fontSize: 10.5,
                                                marginTop: 2,
                                            }}
                                        >
                                            {formatLastSeen(detail.lastSeen)}
                                        </div>
                                    ) : null}
                                </div>
                                <div style={{ padding: '8px 12px' }}>
                                    {detail &&
                                        !detail.error &&
                                        (detail.hoursSeries?.length || 0) >
                                            1 && (
                                            <div style={{ marginBottom: 8 }}>
                                                <div
                                                    style={{
                                                        opacity: 0.6,
                                                        fontSize: 11,
                                                        marginBottom: 2,
                                                    }}
                                                >
                                                    Performance
                                                </div>
                                                <Sparkline
                                                    data={detail.hoursSeries!}
                                                    days={detail.hoursDays}
                                                    alarms={detail.alarms}
                                                    dtcs={detail.dtcs}
                                                    color="#8BC441"
                                                    fmt={(v) =>
                                                        `${
                                                            Math.round(v * 10) /
                                                            10
                                                        } h`
                                                    }
                                                />
                                            </div>
                                        )}
                                    {loadingDetail && !detail && (
                                        <div style={{ opacity: 0.6 }}>
                                            Loading signals…
                                        </div>
                                    )}
                                    {detail &&
                                        !detail.error &&
                                        detail.signals &&
                                        detail.signals.length > 0 && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 7,
                                                }}
                                            >
                                                {detail.signals.map((s, i) =>
                                                    s.kind === 'battery' ? (
                                                        <div
                                                            key={`${s.label}-${i}`}
                                                        >
                                                            <div
                                                                style={{
                                                                    display:
                                                                        'flex',
                                                                    justifyContent:
                                                                        'space-between',
                                                                    gap: 14,
                                                                    marginBottom: 3,
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        opacity: 0.6,
                                                                    }}
                                                                >
                                                                    {s.label}
                                                                </span>
                                                                <span
                                                                    style={{
                                                                        fontWeight: 800,
                                                                        color: batteryColor(
                                                                            s.value
                                                                        ),
                                                                        whiteSpace:
                                                                            'nowrap',
                                                                    }}
                                                                >
                                                                    {s.value}
                                                                    {s.unit
                                                                        ? ` ${s.unit}`
                                                                        : 'V'}
                                                                </span>
                                                            </div>
                                                            {/* Voltage gauge (0–16V scale) */}
                                                            <div
                                                                style={{
                                                                    position:
                                                                        'relative',
                                                                    height: 5,
                                                                    borderRadius: 3,
                                                                    background:
                                                                        'rgba(255,255,255,0.14)',
                                                                    overflow:
                                                                        'hidden',
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        height: '100%',
                                                                        width: `${Math.min(
                                                                            (s.value /
                                                                                16) *
                                                                                100,
                                                                            100
                                                                        )}%`,
                                                                        background:
                                                                            batteryColor(
                                                                                s.value
                                                                            ),
                                                                        borderRadius: 3,
                                                                        transition:
                                                                            'width 0.2s ease',
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            key={`${s.label}-${i}`}
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent:
                                                                    'space-between',
                                                                gap: 14,
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    opacity: 0.6,
                                                                }}
                                                            >
                                                                {s.label}
                                                            </span>
                                                            <span
                                                                style={{
                                                                    fontWeight: 700,
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {s.value.toLocaleString()}
                                                                {s.unit
                                                                    ? ` ${s.unit}`
                                                                    : ''}
                                                            </span>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    {detail &&
                                        !detail.error &&
                                        (!detail.signals ||
                                            detail.signals.length === 0) &&
                                        !loadingDetail && (
                                            <div style={{ opacity: 0.5 }}>
                                                No live signals
                                            </div>
                                        )}
                                    {detail?.error && (
                                        <div style={{ opacity: 0.5 }}>
                                            Detail unavailable
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <div
                            style={{
                                position: 'absolute',
                                ...(tip.vertical === 'top'
                                    ? {
                                          top: '100%',
                                          borderTop:
                                              '6px solid rgba(18,20,23,0.97)',
                                      }
                                    : {
                                          bottom: '100%',
                                          borderBottom:
                                              '6px solid rgba(30,32,36,0.97)',
                                      }),
                                left: `calc(50% - ${tip.dx}px)`,
                                marginLeft: '-6px',
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                            }}
                        />
                    </div>
                )}

                {beacon && (
                    <>
                        <style>{`@keyframes osmoBeacon {0%{transform:translate(-50%,-50%) scale(0.5);opacity:0.55;}100%{transform:translate(-50%,-50%) scale(2.6);opacity:0;}}`}</style>
                        <div
                            style={{
                                position: 'absolute',
                                left: `${size / 2}px`,
                                top: `${size / 2}px`,
                                width: `${size}px`,
                                height: `${size}px`,
                                borderRadius: '50%',
                                backgroundColor:
                                    asset.color ||
                                    getStatusColor(asset.status),
                                animation:
                                    'osmoBeacon 1.8s ease-out infinite',
                                pointerEvents: 'none',
                                zIndex: 0,
                            }}
                        />
                    </>
                )}
                <div
                    ref={dotRef}
                    onClick={handleDotClick}
                    onMouseEnter={handleEnter}
                    onMouseLeave={() => setIsHovered(false)}
                    style={{
                        width: `${size}px`,
                        height: `${size}px`,
                        borderRadius: '50%',
                        backgroundColor:
                            asset.color || getStatusColor(asset.status),
                        border: '2px solid rgba(255,255,255,0.9)',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
                        cursor: 'pointer',
                        transform: isHovered ? 'scale(1.18)' : 'scale(1)',
                        transition: 'transform 0.15s ease',
                        position: 'relative',
                        zIndex: isHovered ? 1001 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: `${Math.max(9, size * 0.34)}px`,
                        fontWeight: 700,
                        lineHeight: 1,
                    }}
                >
                    {countLabel}
                </div>
            </div>
        </OverlayViewF>
    )
})

const UniversalGoogleMapsV2 = ({
    gps,
    style,
    fleetGps,
    mapZoom = 9,
    defaultCenter: defaultCenterProp,
    onAssetClick,
    fetchBoatDetail,
    gpsStatus,
    disableTooltip,
    focusPoint,
    resetView,
    defaultClustered = true,
    maxDeclustered = MAX_DECLUSTERED,
    defaultMapType = 'roadmap',
}: UniversalGoogleMapProps) => {
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { activeManufacturer } = oupUser
    const defaultCenter = useRef(
        defaultCenterProp || {
            lat: 28.446881475873266,
            lng: -81.1580598190508,
        }
    ).current

    const gpsLat = gps ? parseFloat(gps.lat) : NaN
    const gpsLng = gps ? parseFloat(gps.lng) : NaN
    const hasGps = !Number.isNaN(gpsLat) && !Number.isNaN(gpsLng)
    const center = hasGps ? { lat: gpsLat, lng: gpsLng } : defaultCenter

    // The controlled centre is set ONCE on the first valid GPS fix, then left
    // alone so the user can freely pan/zoom — re-renders from telemetry polling
    // won't snap the map back to the pin. The recenter control re-centres on
    // demand.
    const [mapCenter, setMapCenter] = useState(center)
    const centeredOnGpsRef = useRef(false)
    useEffect(() => {
        if (!centeredOnGpsRef.current && hasGps) {
            setMapCenter({ lat: gpsLat, lng: gpsLng })
            centeredOnGpsRef.current = true
        }
    }, [hasGps, gpsLat, gpsLng])

    const containerStyle = {
        width: style.width ?? '100%',
        height: style.height ?? '400px',
        backgroundColor: style.backgroundColor,
        margin: style.margin,
    }

    const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null)
    const [displayedFleetGps, setDisplayedFleetGps] = useState<
        FleetGpsObject[]
    >(fleetGps || [])
    const [, setNeedsFit] = useState(true)
    const [selectedAsset, setSelectedAsset] = useState<FleetGpsObject | null>(
        null
    )
    // Mirror the latest fleetGps so the focus effect can resolve an asset by
    // serial without re-running on every telemetry refresh.
    const fleetGpsRef = useRef(fleetGps)
    fleetGpsRef.current = fleetGps
    // Map type (roadmap <-> satellite) and day/night overlay toggles.
    const [mapType, setMapType] = useState<'roadmap' | 'satellite'>(
        defaultMapType
    )
    const [showDayNight, setShowDayNight] = useState(false)
    // Pins are clustered by default; the user can toggle to individual pins.
    // Callers can start declustered via `defaultClustered={false}`.
    const [clustered, setClustered] = useState(defaultClustered)
    // Serial of a boat focused from outside (its tooltip is forced open).
    const [pinnedSerial, setPinnedSerial] = useState<string | null>(null)
    // Dark map theme toggle (roadmap base only).
    const [darkMap, setDarkMap] = useState(false)
    const [terminator, setTerminator] = useState<
        { lat: number; lng: number }[]
    >([])
    // Current viewport bounds + zoom — used to cull off-screen markers and to
    // cluster adaptively (pins split apart as you zoom in).
    const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null)
    const [zoom, setZoom] = useState(mapZoom)

    const handleRefresh = useCallback(() => {
        setDisplayedFleetGps(fleetGps || [])
        setNeedsFit(true)
        setSelectedAsset(null)
    }, [fleetGps])

    // Re-zoom the map to fit all currently-visible boats (the status-filtered
    // fleet). Single-point fleets get a fixed zoom instead of a max-zoom fit.
    const fitToVisible = useCallback(() => {
        if (!googleMap || !displayedFleetGps?.length) return
        if (displayedFleetGps.length === 1) {
            googleMap.panTo({
                lat: displayedFleetGps[0].lat,
                lng: displayedFleetGps[0].lng,
            })
            googleMap.setZoom(mapZoom)
            return
        }
        const b = new google.maps.LatLngBounds()
        displayedFleetGps.forEach(({ lat, lng }) =>
            b.extend(new google.maps.LatLng(lat, lng))
        )
        googleMap.fitBounds(b)
    }, [googleMap, displayedFleetGps, mapZoom])

    useEffect(() => {
        handleRefresh()
    }, [activeManufacturer, fleetGps])

    const handleAssetClick = useCallback(
        (asset: FleetGpsObject) => {
            setSelectedAsset(asset)
            if (onAssetClick) {
                onAssetClick(asset)
            }
        },
        [onAssetClick]
    )

    useEffect(() => {
        if (!googleMap || !selectedAsset) return

        googleMap.setZoom(18)
        googleMap.setCenter({ lat: selectedAsset.lat, lng: selectedAsset.lng })
    }, [selectedAsset, googleMap])

    // External focus (e.g. a clicked boat row). Sequence matters for big fleets:
    // NAVIGATE first (cheap — imperative pan/zoom), THEN once the map settles at
    // the boat, decluster + open the tooltip. Declustering only after the zoom-in
    // means viewport-culling leaves just a handful of pins to render (instead of
    // the whole fleet at world zoom), and the tooltip is placed against the now-
    // centred pin so it can't fall off the frame.
    useEffect(() => {
        if (!googleMap || !focusPoint) return
        const asset = focusPoint.id
            ? (fleetGpsRef.current || []).find(
                  (a) => a.serial === focusPoint.id
              )
            : null
        if (!asset) {
            setPinnedSerial(null)
            googleMap.panTo({ lat: focusPoint.lat, lng: focusPoint.lng })
            googleMap.setZoom(16)
            return
        }
        googleMap.panTo({ lat: asset.lat, lng: asset.lng })
        googleMap.setZoom(18)
        let cancelled = false
        let h2: google.maps.MapsEventListener | null = null
        const h1 = google.maps.event.addListenerOnce(googleMap, 'idle', () => {
            if (cancelled) return
            // Drop the pin into the lower half so the tooltip (which opens ABOVE
            // it) has headroom and can't clip the top edge of the map.
            googleMap.panBy(0, -110)
            h2 = google.maps.event.addListenerOnce(googleMap, 'idle', () => {
                if (cancelled) return
                setBounds(googleMap.getBounds() || null)
                const z = googleMap.getZoom()
                if (typeof z === 'number') setZoom(z)
                setClustered(false)
                setPinnedSerial(asset.serial || null)
            })
        })
        return () => {
            cancelled = true
            h1.remove()
            if (h2) h2.remove()
        }
    }, [focusPoint, googleMap])

    // Snap back to the default view (e.g. on un-focus / Service row collapse):
    // clear the selection, re-cluster all pins, and zoom out.
    useEffect(() => {
        if (!googleMap || !resetView) return
        googleMap.setCenter(defaultCenterProp || center)
        googleMap.setZoom(mapZoom)
        setSelectedAsset(null)
        setClustered(defaultClustered)
        setPinnedSerial(null)
    }, [resetView, googleMap])

    // Apply the selected map type + dark theme to the live map instance.
    useEffect(() => {
        if (!googleMap) return
        googleMap.setMapTypeId(mapType)
        googleMap.setOptions({
            styles: darkMap && mapType === 'roadmap' ? DARK_MAP_STYLE : [],
        })
    }, [googleMap, mapType, darkMap])

    // Recompute the terminator when enabled, then refresh every 10 min so it
    // tracks real time without re-rendering each second.
    useEffect(() => {
        if (!showDayNight) return
        const update = () => setTerminator(computeTerminator(new Date()))
        update()
        const id = setInterval(update, 10 * 60 * 1000)
        return () => clearInterval(id)
    }, [showDayNight])

    // Track viewport bounds + zoom (debounced by Google's own "idle" event).
    const handleIdle = useCallback(() => {
        if (!googleMap) return
        setBounds(googleMap.getBounds() || null)
        const z = googleMap.getZoom()
        if (typeof z === 'number') setZoom(z)
    }, [googleMap])

    // Cull to the viewport, then cluster adaptively by zoom: pins split apart
    // into smaller clusters / individual boats as you zoom in.
    const visibleGps = useMemo(() => {
        if (!bounds || !displayedFleetGps.length) return displayedFleetGps
        return displayedFleetGps.filter((a) =>
            bounds.contains({ lat: a.lat, lng: a.lng })
        )
    }, [displayedFleetGps, bounds])

    // Too many individual pins in view to render smoothly. When this trips we
    // force clustering regardless of the user's toggle (and disable the toggle),
    // then restore their choice once they zoom back under the threshold.
    const tooManyToDecluster = visibleGps.length > maxDeclustered
    const effectiveClustered = clustered || tooManyToDecluster

    const renderedMarkers = useMemo(
        () =>
            effectiveClustered
                ? clusterPoints(visibleGps, zoom)
                : visibleGps,
        [visibleGps, zoom, effectiveClustered]
    )

    // Clicking a multi-boat cluster zooms in toward it (so it splits apart).
    // For a single boat the marker handles pinning the tooltip itself, so we
    // don't force a zoom here (just forward onAssetClick if a consumer wants it).
    const handleMarkerClick = useCallback(
        (asset: FleetGpsObject) => {
            if ((asset.count ?? 1) > 1 && googleMap) {
                const z = googleMap.getZoom() ?? mapZoom
                googleMap.setZoom(Math.min(z + 2, 18))
                googleMap.panTo({ lat: asset.lat, lng: asset.lng })
                return
            }
            if (onAssetClick) onAssetClick(asset)
        },
        [googleMap, mapZoom, onAssetClick]
    )

    const onMapLoad = useCallback(
        (map) => {
            setGoogleMap(map)
            const bounds = new google.maps.LatLngBounds(center)

            if (defaultCenterProp) {
                // Fleet overview: use a fixed default view (e.g. Atlantic-centred
                // covering the Americas / Europe / Africa) instead of auto-fitting
                // to a globally-spread fleet (which zooms out too far).
                map.setCenter(defaultCenterProp)
                map.setZoom(mapZoom)
            } else if (displayedFleetGps && displayedFleetGps.length > 0) {
                displayedFleetGps.forEach(({ lat, lng }) => {
                    bounds.extend(new google.maps.LatLng(lat, lng))
                })
                map.fitBounds(bounds)
            } else if (gps) {
                // Single location (e.g. one boat): centre on it at a fixed zoom
                // (mapZoom sets the radius) rather than fitBounds, which zooms
                // all the way in to the point.
                map.setCenter(center)
                map.setZoom(mapZoom)
            } else {
                map.setCenter(center)
            }

            const listener: any = googleMap?.addListener('idle', () => {
                const assetCount =
                    (gps ? 1 : 0) + (displayedFleetGps?.length || 0)
                if (assetCount <= 1 && map.getZoom() > mapZoom) {
                    map.setZoom(mapZoom)
                }
                google.maps.event.removeListener(listener)
            })

            setNeedsFit(false)
        },
        [center, displayedFleetGps, gps, fleetGps, mapZoom, defaultCenterProp]
    )

    const onUnmount = useCallback(() => {
        setGoogleMap(null)
    }, [])

    useEffect(() => {
        return () => setGoogleMap(null)
    }, [])

    return (
        <>
            <style>
                {`
                    @keyframes pulse {
                        0% { opacity: 1; }
                        50% { opacity: 0.3; }
                        100% { opacity: 1; }
                    }
                `}
            </style>
            <Grid
                container
                sx={{
                    backgroundColor: style.backgroundColor,
                    margin: style.margin,
                    position: 'relative',
                }}
            >
                <GoogleMapsWrapper>
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={mapCenter}
                        zoom={mapZoom}
                        onLoad={onMapLoad}
                        onUnmount={onUnmount}
                        onIdle={handleIdle}
                        options={{
                            zoomControl: true,
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: true,
                            // 'greedy' = scroll wheel zooms directly (no Ctrl/⌘
                            // needed). Default 'cooperative' requires the modifier.
                            gestureHandling: 'greedy',
                        }}
                    >
                        {showDayNight && terminator.length > 2 && (
                            <PolygonF
                                paths={terminator}
                                options={{
                                    fillColor: '#0b1f3a',
                                    fillOpacity: 0.32,
                                    strokeColor: '#0b1f3a',
                                    strokeOpacity: 0.5,
                                    strokeWeight: 1,
                                    clickable: false,
                                    zIndex: 1,
                                }}
                            />
                        )}

                        {gps && gps.lng && gps.lat && (
                            <CustomMarker
                                position={{ lat: center.lat, lng: center.lng }}
                                asset={{
                                    lat: center.lat,
                                    lng: center.lng,
                                    name: 'Current Location',
                                    status: gpsStatus || 'Offline',
                                    color:
                                        STATUS_DOT[gpsStatus || 'Offline'] ||
                                        STATUS_DOT.Offline,
                                }}
                                onClick={handleAssetClick}
                                disableTooltip={disableTooltip}
                                beacon
                            />
                        )}

                        {renderedMarkers?.map((asset) => (
                            <CustomMarker
                                key={
                                    asset.serial ||
                                    `${asset.lat}-${asset.lng}-${asset.count}`
                                }
                                position={{ lat: asset.lat, lng: asset.lng }}
                                asset={asset}
                                onClick={handleMarkerClick}
                                fetchBoatDetail={fetchBoatDetail}
                                forceOpen={
                                    !!pinnedSerial &&
                                    asset.serial === pinnedSerial &&
                                    (asset.count ?? 1) === 1
                                }
                            />
                        ))}
                    </GoogleMap>
                </GoogleMapsWrapper>

                {/* Map controls: satellite/map + day-night overlay */}
                <div
                    style={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        zIndex: 5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                    }}
                >
                    <Tooltip
                        title={
                            mapType === 'satellite'
                                ? 'Switch to map view'
                                : 'Switch to satellite view'
                        }
                        placement="right"
                    >
                        <IconButton
                            size="small"
                            aria-label={
                                mapType === 'satellite'
                                    ? 'Switch to map view'
                                    : 'Switch to satellite view'
                            }
                            onClick={() =>
                                setMapType((t) =>
                                    t === 'satellite' ? 'roadmap' : 'satellite'
                                )
                            }
                            sx={{
                                backgroundColor:
                                    mapType === 'satellite'
                                        ? '#8BC441'
                                        : '#fff',
                                color:
                                    mapType === 'satellite' ? '#fff' : '#221F1F',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                '&:hover': {
                                    backgroundColor:
                                        mapType === 'satellite'
                                            ? '#7CB037'
                                            : '#f1f1f1',
                                },
                            }}
                        >
                            <Layers size={18} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={
                            showDayNight
                                ? 'Hide day/night'
                                : 'Show day/night'
                        }
                        placement="right"
                    >
                        <IconButton
                            size="small"
                            onClick={() => setShowDayNight((v) => !v)}
                            sx={{
                                backgroundColor: showDayNight
                                    ? '#8BC441'
                                    : '#fff',
                                color: showDayNight ? '#fff' : '#221F1F',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                '&:hover': {
                                    backgroundColor: showDayNight
                                        ? '#7CB037'
                                        : '#f1f1f1',
                                },
                            }}
                        >
                            <Moon size={18} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={darkMap ? 'Light map' : 'Dark map'}
                        placement="right"
                    >
                        <IconButton
                            size="small"
                            onClick={() => setDarkMap((v) => !v)}
                            sx={{
                                backgroundColor: darkMap ? '#221F1F' : '#fff',
                                color: darkMap ? '#fff' : '#221F1F',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                '&:hover': {
                                    backgroundColor: darkMap
                                        ? '#000'
                                        : '#f1f1f1',
                                },
                            }}
                        >
                            <Contrast size={18} />
                        </IconButton>
                    </Tooltip>
                    {!hasGps && (
                        <Tooltip
                            title={
                                tooManyToDecluster
                                    ? `Zoom in to show individual pins (≤${maxDeclustered} boats in view)`
                                    : effectiveClustered
                                    ? 'Show individual pins'
                                    : 'Cluster pins'
                            }
                            placement="right"
                        >
                            {/* span wrapper keeps the tooltip working while the
                                button is disabled (disabled elements emit no
                                pointer events of their own). */}
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={tooManyToDecluster}
                                    onClick={() => setClustered((v) => !v)}
                                    sx={{
                                        backgroundColor: effectiveClustered
                                            ? '#fff'
                                            : '#8BC441',
                                        color: effectiveClustered
                                            ? '#221F1F'
                                            : '#fff',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                        '&.Mui-disabled': {
                                            backgroundColor: '#fff',
                                            color: 'rgba(0,0,0,0.26)',
                                        },
                                        '&:hover': {
                                            backgroundColor: effectiveClustered
                                                ? '#f1f1f1'
                                                : '#7CB037',
                                        },
                                    }}
                                >
                                    <Ungroup size={18} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    {hasGps && (
                        <Tooltip title="Recenter on boat" placement="right">
                            <IconButton
                                size="small"
                                onClick={() => {
                                    if (googleMap) {
                                        googleMap.panTo({
                                            lat: gpsLat,
                                            lng: gpsLng,
                                        })
                                        googleMap.setZoom(mapZoom)
                                    }
                                }}
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#221F1F',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                    '&:hover': { backgroundColor: '#f1f1f1' },
                                }}
                            >
                                <Crosshair size={18} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!hasGps && (displayedFleetGps?.length || 0) > 0 && (
                        <Tooltip
                            title="Fit to visible boats"
                            placement="right"
                        >
                            <IconButton
                                size="small"
                                onClick={fitToVisible}
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#221F1F',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                    '&:hover': { backgroundColor: '#f1f1f1' },
                                }}
                            >
                                <Maximize size={18} />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </Grid>
        </>
    )
}

export default memo(UniversalGoogleMapsV2)
